/**
 * Commandes issues d'un plan, et réception en deux temps — les appels (V1.6-09).
 *
 * Trois règles, les mêmes qu'en V1.6-08 et pour les mêmes raisons :
 *
 * 1. **aucun corps de requête ne porte d'argent.** Créer les commandes n'envoie
 *    rien du tout ; compter une réception n'envoie qu'une quantité et un
 *    endroit. Prix, transport, totaux et reliquats sont calculés par le serveur,
 *    et c'est la seule façon d'empêcher qu'un chiffre affiché puis modifié dans
 *    un navigateur devienne un engagement ;
 * 2. **l'écran ne décide d'aucun droit.** `can_mark_ordered`, `can_cancel`,
 *    `can_receive` et `can_finalize` viennent du serveur. Un bouton grisé est
 *    une politesse ; l'autorisation, elle, est vérifiée là où elle compte ;
 * 3. **la péremption et la sur-réception ne sont jamais déduites ici.** Créer
 *    une commande sur un plan périmé est refusé par le serveur avec
 *    `STALE_APPROVED_PLAN`, et finaliser une livraison excédentaire avec
 *    `OVER_RECEIPT_ACK_REQUIRED`. Il n'existe pas de `force` : la réponse est
 *    de relire, ou d'accepter nommément.
 *
 * `staleTime: 0` partout, comme pour les plans : ce qui change une commande se
 * passe ailleurs — un camion arrive, un collègue finalise — et une fiche servie
 * depuis un cache tiède ferait recompter ce qui est déjà entré.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "./client";
import type {
  AcknowledgementHistory,
  AcknowledgementResult,
  AcknowledgementWrite,
  CountedLineInput,
  PlannedPurchaseOrderDetail,
  PlannedPurchaseOrderPage,
  PlannedReceiptDetail,
  PlannedReceiptPage,
  PurchaseOrderDispatch,
  RemainderAbandonInput,
  ReturnableLine,
  Uuid,
} from "./types";

export const orderKeys = {
  all: ["planned-purchase-orders"] as const,
  list: (params: object) => ["planned-purchase-orders", "list", params] as const,
  forPlan: (planId: Uuid) => ["planned-purchase-orders", "plan", planId] as const,
  one: (orderId: Uuid) => ["planned-purchase-orders", orderId] as const,
  receipts: (orderId: Uuid) => ["planned-purchase-orders", orderId, "receipts"] as const,
  dispatch: (orderId: Uuid) => ["planned-purchase-orders", orderId, "dispatch"] as const,
  acknowledgements: (orderId: Uuid) =>
    ["planned-purchase-orders", orderId, "acknowledgements"] as const,
  receipt: (receiptId: Uuid) => ["draft-receipts", receiptId] as const,
  returnable: (orderId: Uuid) => ["planned-purchase-orders", orderId, "returnable"] as const,
};

export interface PlannedOrderParams {
  [key: string]: unknown;
  plan_id?: Uuid;
  limit?: number;
  offset?: number;
}

function query(path: string, params?: Record<string, unknown>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const suffix = search.toString();
  return suffix ? `${path}?${suffix}` : path;
}

/**
 * Toutes les commandes nées d'un plan.
 *
 * La liste ne dit **pas** la progression ligne par ligne : la calculer pour
 * chaque bon demanderait de sommer toutes les réceptions de tous les bons, et
 * c'est cher pour un chiffre que la fiche donne exactement.
 */
export function usePlannedPurchaseOrders(params: PlannedOrderParams = {}) {
  return useQuery({
    queryKey: orderKeys.list(params),
    queryFn: () =>
      apiFetch<PlannedPurchaseOrderPage>(query("/planned-purchase-orders", params)),
    staleTime: 0,
  });
}

export function usePlanPurchaseOrders(planId: Uuid | undefined) {
  return useQuery({
    queryKey: orderKeys.forPlan(planId ?? ""),
    queryFn: () =>
      apiFetch<PlannedPurchaseOrderPage>(`/purchase-plans/${planId}/purchase-orders`),
    enabled: Boolean(planId),
    staleTime: 0,
  });
}

export function usePlannedPurchaseOrder(orderId: Uuid | undefined) {
  return useQuery({
    queryKey: orderKeys.one(orderId ?? ""),
    queryFn: () => apiFetch<PlannedPurchaseOrderDetail>(`/purchase-orders/${orderId}/detail`),
    enabled: Boolean(orderId),
    staleTime: 0,
  });
}

export function useOrderReceipts(orderId: Uuid | undefined) {
  return useQuery({
    queryKey: orderKeys.receipts(orderId ?? ""),
    queryFn: () => apiFetch<PlannedReceiptPage>(`/purchase-orders/${orderId}/draft-receipts`),
    enabled: Boolean(orderId),
    staleTime: 0,
  });
}

export function useDraftReceipt(receiptId: Uuid | undefined) {
  return useQuery({
    queryKey: orderKeys.receipt(receiptId ?? ""),
    queryFn: () => apiFetch<PlannedReceiptDetail>(`/draft-receipts/${receiptId}`),
    enabled: Boolean(receiptId),
    staleTime: 0,
  });
}

/**
 * Ce qu'une commande permet encore de renvoyer au fournisseur.
 *
 * Borne commerciale seulement : reçu moins déjà renvoyé. Que le stock existe
 * encore à l'endroit choisi et n'y soit pas réservé, c'est l'inventaire qui le
 * dit au moment de finaliser — l'écran ne refait jamais ce calcul.
 */
export function useReturnableLines(orderId: Uuid | undefined) {
  return useQuery({
    queryKey: orderKeys.returnable(orderId ?? ""),
    queryFn: () => apiFetch<ReturnableLine[]>(`/purchase-orders/${orderId}/returnable-lines`),
    enabled: Boolean(orderId),
    staleTime: 0,
  });
}

function invalidateOrder(
  queryClient: ReturnType<typeof useQueryClient>,
  orderId: Uuid | undefined,
) {
  void queryClient.invalidateQueries({ queryKey: orderKeys.all });
  if (orderId) {
    void queryClient.invalidateQueries({ queryKey: orderKeys.one(orderId) });
    void queryClient.invalidateQueries({ queryKey: orderKeys.receipts(orderId) });
    void queryClient.invalidateQueries({ queryKey: orderKeys.returnable(orderId) });
    void queryClient.invalidateQueries({ queryKey: orderKeys.dispatch(orderId) });
    void queryClient.invalidateQueries({ queryKey: orderKeys.acknowledgements(orderId) });
  }
}

/**
 * Créer les commandes d'un plan approuvé. **Corps vide, et c'est voulu.**
 *
 * Un bon par soumission retenue. Rappelée, elle rend les mêmes : l'idempotence
 * tient à un unique en base, donc un double clic ne crée pas deux engagements.
 */
export function useCreateOrdersFromPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ planId }: { planId: Uuid }) =>
      apiFetch<PlannedPurchaseOrderPage>(`/purchase-plans/${planId}/purchase-orders`, {
        method: "POST",
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: orderKeys.all });
      void queryClient.invalidateQueries({ queryKey: orderKeys.forPlan(variables.planId) });
    },
  });
}

/** Confirmer qu'un humain a réellement passé la commande. Aucun stock ne bouge. */
export function useMarkOrdered() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId }: { orderId: Uuid }) =>
      apiFetch<PlannedPurchaseOrderDetail>(`/purchase-orders/${orderId}/mark-ordered`, {
        method: "POST",
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(orderKeys.one(variables.orderId), data);
      invalidateOrder(queryClient, variables.orderId);
    },
  });
}

export function useCancelPlannedOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, reason }: { orderId: Uuid; reason: string }) =>
      apiFetch<PlannedPurchaseOrderDetail>(`/purchase-orders/${orderId}/cancel-order`, {
        method: "POST",
        body: { reason },
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(orderKeys.one(variables.orderId), data);
      invalidateOrder(queryClient, variables.orderId);
    },
  });
}

/** Enregistrer ce qui a été compté sur le quai. **Rien n'entre en stock.** */
export function useCreateDraftReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      lines,
      supplierReference,
      notes,
    }: {
      orderId: Uuid;
      lines: CountedLineInput[];
      supplierReference?: string | null;
      notes?: string | null;
    }) =>
      apiFetch<PlannedReceiptDetail>(`/purchase-orders/${orderId}/draft-receipts`, {
        method: "POST",
        body: {
          lines,
          supplier_reference: supplierReference || null,
          notes: notes || null,
        },
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(orderKeys.receipt(data.receipt.id), data);
      invalidateOrder(queryClient, variables.orderId);
    },
  });
}

export function useUpdateDraftReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ receiptId, lines }: { receiptId: Uuid; lines: CountedLineInput[] }) =>
      apiFetch<PlannedReceiptDetail>(`/draft-receipts/${receiptId}`, {
        method: "PATCH",
        body: { lines },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(orderKeys.receipt(data.receipt.id), data);
      invalidateOrder(queryClient, data.receipt.purchase_order_id);
    },
  });
}

/**
 * Finaliser : **le seul chemin vers le grand livre.**
 *
 * `acknowledgements` reste vide dans le cas ordinaire. Il ne porte
 * `"OVER_RECEIPT"` que lorsqu'un humain a vu ce qui dépasse et l'a accepté —
 * il n'y a pas de drapeau global, et accepter une sur-réception c'est dire
 * laquelle.
 */
export function useFinalizeReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      receiptId,
      acknowledgements,
    }: {
      receiptId: Uuid;
      acknowledgements?: string[];
    }) =>
      apiFetch<PlannedReceiptDetail>(`/draft-receipts/${receiptId}/finalize`, {
        method: "POST",
        body: { acknowledgements: acknowledgements ?? [] },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(orderKeys.receipt(data.receipt.id), data);
      invalidateOrder(queryClient, data.receipt.purchase_order_id);
      // Une finalisation a bougé du stock : les écrans d'inventaire mentent
      // jusqu'à ce qu'ils relisent.
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/**
 * Renoncer à un reliquat. **Aucun stock ne bouge.**
 *
 * La quantité est explicite et jamais « tout le reste » : le reste est un
 * chiffre lu à un instant, et deux personnes qui cliquent en même temps ont lu
 * deux instants différents. L'écran propose le reliquat courant comme valeur
 * de départ ; c'est le serveur, sous verrou, qui refuse ce qui dépasse.
 */
export function useAbandonRemainder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, ...body }: { orderId: Uuid } & RemainderAbandonInput) =>
      apiFetch<PlannedPurchaseOrderDetail>(`/purchase-orders/${orderId}/remainder-actions`, {
        method: "POST",
        body,
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(orderKeys.one(variables.orderId), data);
      invalidateOrder(queryClient, variables.orderId);
      // Renoncer change ce qu'on attend, donc le besoin net : ce qui n'est plus
      // en route redevient à racheter.
      void queryClient.invalidateQueries({ queryKey: ["net-requirements"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}


/**
 * Cinq secondes : assez court pour qu'un envoi paraisse suivi, assez long pour
 * ne pas marteler le serveur. Le traitement est synchrone aujourd'hui, donc ce
 * sondage ne sert qu'aux etats transitoires laisses par un geste interrompu —
 * d'ou un intervalle sobre plutot qu'agressif.
 */
export const DISPATCH_POLL_MS = 5_000;

/**
 * Ou en est la transmission de ce bon (V1.6-14.3).
 *
 * Une lecture, et elle ne declenche rien. L'intervalle de rafraichissement est
 * **conditionnel** : seul un envoi reellement en vol — `QUEUED` ou `SENDING` —
 * vaut qu'on revienne voir. Sur un etat terminal la requete se tait, parce
 * qu'interroger un fait qui ne bougera plus est du bruit.
 */
export function usePurchaseOrderDispatch(orderId: Uuid | undefined) {
  return useQuery({
    queryKey: orderKeys.dispatch(orderId ?? ""),
    enabled: Boolean(orderId),
    queryFn: () => apiFetch<PurchaseOrderDispatch>(`/purchase-orders/${orderId}/dispatch`),
    refetchInterval: (query) => {
      const state = query.state.data?.dispatch_state;
      return state === "QUEUED" || state === "SENDING" ? DISPATCH_POLL_MS : false;
    },
  });
}

/**
 * Le geste humain : preparer le document, puis le transmettre.
 *
 * **Corps vide, et c'est voulu.** Ni destinataire, ni objet, ni texte ne
 * viennent d'ici : tout est derive du bon fige cote serveur. Le frontend ne
 * reconstruit jamais l'adresse, il affiche celle que le serveur a resolue.
 */
export function useQueuePurchaseOrderSend() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId }: { orderId: Uuid }) =>
      apiFetch<PurchaseOrderDispatch>(`/purchase-orders/${orderId}/queue-send`, {
        method: "POST",
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(orderKeys.dispatch(variables.orderId), data);
      // Le bon lui-meme a pu passer a `ORDERED` : les deux ressources doivent
      // bouger ensemble, sinon l'ecran montre « Envoye » a cote de « Brouillon ».
      invalidateOrder(queryClient, variables.orderId);
    },
  });
}

/** Reprendre un envoi **qui a echoue net**. Jamais une livraison incertaine. */
export function useRetryPurchaseOrderSend() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId }: { orderId: Uuid }) =>
      apiFetch<PurchaseOrderDispatch>(`/purchase-orders/${orderId}/retry-send`, {
        method: "POST",
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(orderKeys.dispatch(variables.orderId), data);
      invalidateOrder(queryClient, variables.orderId);
    },
  });
}


/**
 * Ce que le fournisseur a repondu, et tout ce qui l'a produit (V1.6-14.6).
 *
 * **Aucun sondage.** Contrairement a l'envoi, rien n'arrive tout seul ici : une
 * reponse fournisseur entre par une saisie humaine, donc il n'y a rien a
 * attendre en boucle. Interroger un fait qui ne bougera que par un clic serait
 * du bruit.
 */
export function usePurchaseOrderAcknowledgements(orderId: Uuid | undefined) {
  return useQuery({
    queryKey: orderKeys.acknowledgements(orderId ?? ""),
    enabled: Boolean(orderId),
    queryFn: () =>
      apiFetch<AcknowledgementHistory>(`/purchase-orders/${orderId}/acknowledgements`),
  });
}

/**
 * Enregistrer ce que le fournisseur a dit. **Et rien d'autre.**
 *
 * Le bon n'est pas modifie, aucun message ne part, aucun stock ne bouge — meme
 * pour un refus. L'ecran invalide quand meme le bon complet : l'etat d'accuse
 * s'y affiche, et une lecture perimee ferait croire qu'il ne s'est rien passe.
 */
export function useRecordSupplierAcknowledgement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: Uuid; body: AcknowledgementWrite }) =>
      apiFetch<AcknowledgementResult>(`/purchase-orders/${orderId}/acknowledgements`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, variables) => {
      invalidateOrder(queryClient, variables.orderId);
    },
  });
}
